// Phase: "stopped" — robot has paused mid-navigation, showing progress at
// stop time. User can Resume navigation or Return to Base.
import React from "react";
import { Box, Stack, Avatar, Typography, Button } from "@mui/material";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import ReplayIcon from "@mui/icons-material/Replay";
import HomeIcon from "@mui/icons-material/Home";
import PropTypes from "prop-types";

import DeliveryLayout from "../../../components/delivery/DeliveryLayout";
import SectionCard from "../../../components/delivery/SectionCard";
import ActionButtonRow from "../../../components/delivery/ActionButtonRow";
import DoorControl from "../../../components/delivery/DoorControl";
import ProgressBar from "../../../components/delivery/ProgressBar";
import StopInfoStrip from "../../../components/delivery/StopInfoStrip";
import { actionBtnSx } from "../deliveryStyles";
import { tokens } from "../../../theme";
import BoxedInNotice from "../../../components/delivery/BoxedInNotice";

// Copy for a leg that ended because the robot gave up boxed in, vs. a plain
// pause. Picked once rather than as a ternary per string.
const COPY = {
  paused: {
    title: "Navigation Paused",
    subtitle: "Robot is stationary — awaiting instruction",
    resumeLabel: "Resume",
    detail: "Resume to continue or return to base.",
  },
  boxedIn: {
    title: "Couldn't Get Through",
    subtitle: "Boxed in — the robot needs manual help",
    resumeLabel: "Try again",
    detail: "Clear the way around the robot, then try again or return to base.",
  },
};

const StatusNotice = () => (
  <Box
    sx={{
      p: 1.5,
      background: tokens.warningSoft,
      border: `1px solid ${tokens.warning}`,
      borderRadius: "10px",
    }}
  >
    <Stack direction="row" alignItems="center" gap={1}>
      <PauseCircleIcon sx={{ fontSize: 15, color: tokens.warning }} />
      <Typography sx={{ fontSize: 12, fontWeight: 500, color: tokens.warning }}>
        Robot is stationary — awaiting your instruction
      </Typography>
    </Stack>
  </Box>
);

const StoppedPhase = ({
  selectedMap,
  navigationTarget,
  robot,
  fromRobotId,
  batteryLevel,
  batteryCharging,
  showMapSelectionModalDialog,
  closeMapSelectionDialogModal,
  portalRobots,
  onRobotChange,
  onEmergencyResume,
  isRobotConnected,
  isDoorOpen,
  onDoorPress,
  isAeroRobot,
  stoppedProgress,
  currentDest,
  onResume,
  onReturnToBase,
  boxedIn = null,
  boxedInFailure = false,
  onRetry,
}) => {
  const copy = boxedInFailure ? COPY.boxedIn : COPY.paused;
  // After a boxed-in give-up the goal was dropped, so Resume would not move it.
  const handlePrimary = boxedInFailure ? onRetry : onResume;
  return (
    <DeliveryLayout
      selectedMap={selectedMap}
      navigationTarget={navigationTarget}
      robot={robot}
      fromRobotId={fromRobotId}
      batteryLevel={batteryLevel}
      batteryCharging={batteryCharging}
      showMapSelectionModalDialog={showMapSelectionModalDialog}
      closeMapSelectionDialogModal={closeMapSelectionDialogModal}
      portalRobots={portalRobots}
      onRobotChange={onRobotChange}
      onEmergencyResume={onEmergencyResume}
      isRobotConnected={isRobotConnected}
      navProgress={stoppedProgress}
      isStopped
      rightPanel={
        <SectionCard
          icon={
            <Avatar
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                background: "rgba(255,255,255,0.15)",
              }}
            >
              <PauseCircleIcon sx={{ fontSize: 20, color: "#fff" }} />
            </Avatar>
          }
          title={copy.title}
          subtitle={copy.subtitle}
          headerBg="linear-gradient(135deg, #431407 0%, #92400e 100%)"
        >
          <DoorControl
            isDoorOpen={isDoorOpen}
            onDoorPress={onDoorPress}
            isAeroRobot={isAeroRobot}
          />

          <StopInfoStrip from="Robot's last position" to={currentDest?.name} />

          {/* Stop details */}
          <Box
            sx={{
              p: 2,
              background: tokens.surface2,
              border: `1px solid ${tokens.border}`,
              borderRadius: "12px",
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: tokens.text, mb: 0.5 }}>
              Navigation Stopped
            </Typography>
            <Typography sx={{ fontSize: 12, color: tokens.text2, lineHeight: 1.6 }}>
              Progress was at <strong>{stoppedProgress}%</strong> toward{" "}
              <strong>{currentDest?.name ?? "destination"}</strong>. {copy.detail}
            </Typography>
          </Box>

          {/* Progress bar frozen at stop point */}
          <ProgressBar
            value={stoppedProgress}
            color={`linear-gradient(90deg,${tokens.warning},${tokens.warning})`}
            label="Progress at stop"
          />

          {/* Status notice — the boxed-in alert replaces it while one is open */}
          {boxedIn ? <BoxedInNotice view={boxedIn} showTarget={false} /> : <StatusNotice />}

          {/* Resume + Return to Base */}
          <ActionButtonRow>
            <Button
              variant="contained"
              onClick={handlePrimary}
              startIcon={<ReplayIcon sx={{ fontSize: "16px !important" }} />}
              sx={{
                ...actionBtnSx,
                background: tokens.accent,
                color: "#fff",
                "&:hover": { background: tokens.accent2 },
              }}
            >
              {copy.resumeLabel}
            </Button>
            <Button
              variant="contained"
              onClick={onReturnToBase}
              startIcon={<HomeIcon sx={{ fontSize: "16px !important" }} />}
              sx={{
                ...actionBtnSx,
                background: tokens.success,
                color: "#fff",
                "&:hover": { background: tokens.success },
              }}
            >
              Return to Base
            </Button>
          </ActionButtonRow>
        </SectionCard>
      }
    />
  );
};

StoppedPhase.propTypes = {
  selectedMap: PropTypes.object,
  navigationTarget: PropTypes.object,
  robot: PropTypes.object,
  fromRobotId: PropTypes.string,
  batteryLevel: PropTypes.number,
  batteryCharging: PropTypes.bool,
  showMapSelectionModalDialog: PropTypes.bool,
  closeMapSelectionDialogModal: PropTypes.func,
  portalRobots: PropTypes.array,
  onRobotChange: PropTypes.func,
  onEmergencyResume: PropTypes.func,
  isRobotConnected: PropTypes.bool,
  isDoorOpen: PropTypes.bool,
  onDoorPress: PropTypes.func,
  isAeroRobot: PropTypes.bool,
  stoppedProgress: PropTypes.number,
  currentDest: PropTypes.object,
  onResume: PropTypes.func,
  onReturnToBase: PropTypes.func,
  /** describeBoxedIn() view from useBoxedIn, or null when not boxed in. */
  boxedIn: PropTypes.object,
  /** The leg ended in a boxed-in give-up (code BOXED_IN). */
  boxedInFailure: PropTypes.bool,
  /** Re-sends the current leg; used instead of onResume after a give-up. */
  onRetry: PropTypes.func,
};

export default StoppedPhase;
